/**
 * Evaluador seguro de expresiones matemáticas para campos de fórmula.
 *
 * Parsea y evalúa expresiones aritméticas usando un parser recursivo descendente.
 * NO utiliza eval(), Function() ni ningún mecanismo de ejecución dinámica.
 *
 * Gramática soportada (EBNF):
 *   expression = term (('+' | '-') term)*
 *   term       = power (('*' | '/') power)*
 *   power      = unary ('**' power)?
 *   unary      = '-' unary | atom
 *   atom       = NUMBER | '(' expression ')'
 *   NUMBER     = [0-9]+ ('.' [0-9]+)?
 */

/**
 * Evalúa una expresión de fórmula de forma segura.
 *
 * @param {string} formula - Expresión con referencias {id_campo}, ej: "{peso} / ({altura} * {altura})"
 * @param {Object} valores - Mapa de id_campo -> valor (string o número)
 * @returns {{ value: number|null, error: string|null }}
 */
export function evaluateFormula(formula, valores) {
  try {
    if (!formula || typeof formula !== 'string') {
      return { value: null, error: 'Expresión inválida' };
    }

    // 1. Extraer todas las referencias {id_campo} de la fórmula
    const refRegex = /\{([^}]+)\}/g;
    const referencias = [];
    let match;
    while ((match = refRegex.exec(formula)) !== null) {
      referencias.push(match[1]);
    }

    // 2. Verificar que TODAS las referencias tienen valores numéricos
    //    Checkboxes: undefined/null/false → 0 solo para cálculo (no afecta impresión)
    for (const id of referencias) {
      const val = valores ? valores[id] : undefined;
      if (val === undefined || val === null || val === false) {
        // Checkbox no tocado o desmarcado → 0 para cálculo
        continue;
      }
      if (val === true) {
        // Checkbox marcado → 1 para cálculo
        continue;
      }
      if (val === '') {
        return { value: null, error: `Faltan datos: ${id}` };
      }
      const num = Number(val);
      if (isNaN(num)) {
        return { value: null, error: `Faltan datos: ${id}` };
      }
    }

    // 3. Reemplazar referencias con valores numéricos
    //    Checkboxes: true → 1, false/undefined/null → 0
    let expresion = formula.replace(refRegex, (_, id) => {
      const val = valores ? valores[id] : undefined;
      if (val === true) return '1';
      if (val === false || val === undefined || val === null) return '0';
      return String(Number(val));
    });

    // 4. Validar que solo contenga caracteres permitidos
    //    Dígitos, operadores (+, -, *, /), paréntesis, puntos decimales, espacios
    if (!/^[\d+\-*/().\s]*$/.test(expresion)) {
      return { value: null, error: 'Caracteres no permitidos' };
    }

    // 5. Tokenizar
    const tokens = tokenize(expresion);
    if (tokens === null) {
      return { value: null, error: 'Expresión inválida' };
    }

    // 6. Parsear y evaluar
    const parser = new Parser(tokens);
    const result = parser.parseExpression();

    // Verificar que se consumieron todos los tokens
    if (parser.pos < tokens.length) {
      return { value: null, error: 'Expresión inválida' };
    }

    if (result.error) {
      return { value: null, error: result.error };
    }

    if (!isFinite(result.value)) {
      return { value: null, error: 'Expresión inválida' };
    }

    return { value: result.value, error: null };
  } catch (e) {
    return { value: null, error: 'Expresión inválida' };
  }
}


/**
 * Tokeniza una expresión matemática en una lista de tokens.
 * Tokens posibles: { type: 'number', value: <number> },
 *                  { type: 'op', value: '+' | '-' | '*' | '/' | '**' },
 *                  { type: 'lparen' }, { type: 'rparen' }
 *
 * @param {string} expr - Expresión ya con valores numéricos sustituidos
 * @returns {Array|null} Lista de tokens o null si hay error
 */
function tokenize(expr) {
  const tokens = [];
  let i = 0;

  while (i < expr.length) {
    const ch = expr[i];

    // Espacios
    if (ch === ' ' || ch === '\t') {
      i++;
      continue;
    }

    // Números (incluyendo decimales)
    if (ch >= '0' && ch <= '9' || ch === '.') {
      let numStr = '';
      let hasDot = false;
      while (i < expr.length && ((expr[i] >= '0' && expr[i] <= '9') || expr[i] === '.')) {
        if (expr[i] === '.') {
          if (hasDot) return null; // Doble punto decimal
          hasDot = true;
        }
        numStr += expr[i];
        i++;
      }
      const num = Number(numStr);
      if (isNaN(num)) return null;
      tokens.push({ type: 'number', value: num });
      continue;
    }

    // Operador ** (potencia) - debe chequearse antes de *
    if (ch === '*' && i + 1 < expr.length && expr[i + 1] === '*') {
      tokens.push({ type: 'op', value: '**' });
      i += 2;
      continue;
    }

    // Operadores simples
    if (ch === '+' || ch === '-' || ch === '*' || ch === '/') {
      tokens.push({ type: 'op', value: ch });
      i++;
      continue;
    }

    // Paréntesis
    if (ch === '(') {
      tokens.push({ type: 'lparen' });
      i++;
      continue;
    }
    if (ch === ')') {
      tokens.push({ type: 'rparen' });
      i++;
      continue;
    }

    // Carácter no reconocido
    return null;
  }

  return tokens;
}

/**
 * Parser recursivo descendente para expresiones aritméticas.
 */
class Parser {
  constructor(tokens) {
    this.tokens = tokens;
    this.pos = 0;
  }

  peek() {
    return this.pos < this.tokens.length ? this.tokens[this.pos] : null;
  }

  consume() {
    return this.tokens[this.pos++];
  }

  /**
   * expression = term (('+' | '-') term)*
   */
  parseExpression() {
    let left = this.parseTerm();
    if (left.error) return left;

    while (this.peek() && this.peek().type === 'op' &&
           (this.peek().value === '+' || this.peek().value === '-')) {
      const op = this.consume().value;
      const right = this.parseTerm();
      if (right.error) return right;

      if (op === '+') {
        left = { value: left.value + right.value, error: null };
      } else {
        left = { value: left.value - right.value, error: null };
      }
    }

    return left;
  }

  /**
   * term = power (('*' | '/') power)*
   */
  parseTerm() {
    let left = this.parsePower();
    if (left.error) return left;

    while (this.peek() && this.peek().type === 'op' &&
           (this.peek().value === '*' || this.peek().value === '/')) {
      const op = this.consume().value;
      const right = this.parsePower();
      if (right.error) return right;

      if (op === '*') {
        left = { value: left.value * right.value, error: null };
      } else {
        if (right.value === 0) {
          return { value: null, error: 'División por cero' };
        }
        left = { value: left.value / right.value, error: null };
      }
    }

    return left;
  }

  /**
   * power = unary ('**' power)?
   * Nota: ** es asociativo a la derecha, por eso se usa recursión
   */
  parsePower() {
    let base = this.parseUnary();
    if (base.error) return base;

    if (this.peek() && this.peek().type === 'op' && this.peek().value === '**') {
      this.consume();
      const exp = this.parsePower(); // Recursión derecha
      if (exp.error) return exp;
      base = { value: Math.pow(base.value, exp.value), error: null };
    }

    return base;
  }

  /**
   * unary = '-' unary | atom
   */
  parseUnary() {
    if (this.peek() && this.peek().type === 'op' && this.peek().value === '-') {
      this.consume();
      const operand = this.parseUnary();
      if (operand.error) return operand;
      return { value: -operand.value, error: null };
    }
    return this.parseAtom();
  }

  /**
   * atom = NUMBER | '(' expression ')'
   */
  parseAtom() {
    const token = this.peek();

    if (!token) {
      return { value: null, error: 'Expresión inválida' };
    }

    // Número
    if (token.type === 'number') {
      this.consume();
      return { value: token.value, error: null };
    }

    // Paréntesis
    if (token.type === 'lparen') {
      this.consume();
      const result = this.parseExpression();
      if (result.error) return result;

      const closing = this.peek();
      if (!closing || closing.type !== 'rparen') {
        return { value: null, error: 'Paréntesis desbalanceados' };
      }
      this.consume();
      return result;
    }

    return { value: null, error: 'Expresión inválida' };
  }
}
